import { REGEX } from '@common/constants/global.constants'
import { TRAINEE_STATUSES } from '@common/constants/person.constants'
import { SelectOptionType } from '@common/enums/global.enums'
import { Trainee, TraineeFormValues } from '@common/types/person.types'
import { getCompanyLabel } from '@common/utils/company.utils'
import Button from '@components/arthurBased/inputs/Button'
import TextInput from '@components/arthurBased/inputs/TextInput'
import Icon from '@components/dataDisplay/Icon'
import CompanySelectInput from '@components/inputs/CompanySelectInput'
import SelectInput from '@components/inputs/SelectInput'
import Loader from '@components/state/Loader'
import { Route } from '@config/routes/routes.enums'
import { FontWeight } from '@config/theme/sizes'
import { ArrowBackRounded } from '@mui/icons-material'
import { Stack, styled, Typography } from '@mui/material'
import { usePersonApi } from '@services/api/person.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import TraineeDeletionPopIn from './TraineeDeletionPopIn'

const TraineeInformation = () => {
	const { formatMessage } = useIntl()
	const { isAdmin } = useAuth()
	const navigate = useNavigate()
	const { id } = useParams()
	const { getPersonById, updateTrainee } = usePersonApi()
	const { addErrorSnackbar, addSnackbar } = useSnackbar()
	const [isDeletionPopInOpen, setIsDeletionPopInOpen] = useState(false)

	const { data: trainee, isLoading, refetch } = useQuery({
		queryKey: ['getPersonById', id],
		queryFn: () => id ? getPersonById(id) : undefined
	})

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		watch
	} = useForm<TraineeFormValues>({
		defaultValues: {
			firstname: trainee?.firstname,
			lastname: '',
			email: '',
			company: {
				label: '',
				value: ''
			},
			status: ''
		}
	})

	const { mutate: updateTraineeMutate } = useMutation<Trainee, Error, { id: string, values: TraineeFormValues }>({
		mutationKey: ['updateTrainee'],
		mutationFn: ({ id, values }) => updateTrainee(id, values),
		onSuccess: () => {
			refetch()
			addSnackbar(formatMessage({ id: 'trainee.information.buttons.save.success' }))
		},
		onError: (error) => addErrorSnackbar(formatMessage({ id: error.message }))
	})

	const onSubmit = useCallback((values: TraineeFormValues) => {
		if (id) {
			updateTraineeMutate({ id, values })
		}
	}, [id])

	useEffect(() => {
		if (trainee) {
			reset({
				firstname: trainee.firstname,
				lastname: trainee.lastname,
				email: trainee.email,
				company: {
					label: getCompanyLabel(trainee.company, formatMessage, isAdmin),
					value: trainee.company.id
				},
				status: trainee.status
			})
		}
	}, [trainee])

	if (isLoading) {
		return <Loader />
	}

	return (
		<Container>
			<Title>
				<IconContainer>
					<Icon IconElement={ArrowBackRounded} onClick={() => navigate(`/${Route.TRAINEES}`)} />
				</IconContainer>
				<Typography variant="h4" fontWeight={FontWeight.SEMI_BOLD}>
					{`${trainee?.firstname} ${trainee?.lastname}`}
				</Typography>
			</Title>
			<Form>
				<TextInput
					control={control}
					name="firstname"
					label={formatMessage({ id: 'global.fields.firstname' })}
					rules={{
						maxLength: 255
					}}
					isRequired
				/>
				<TextInput
					control={control}
					name="lastname"
					label={formatMessage({ id: 'global.fields.lastname' })}
					rules={{
						maxLength: 255
					}}
					isRequired
				/>
				<TextInput
					control={control}
					name="email"
					label={formatMessage({ id: 'global.fields.email' })}
					rules={{
						maxLength: 255,
						pattern: REGEX.EMAIL
					}}
				/>
				<CompanySelectInput
					control={control}
					name="company.value"
					label={formatMessage({ id: 'global.fields.company' })}
					companyLabel={watch('company.label')}
					setCompanyLabel={(value) => setValue('company.label', value)}
					isRequired
					isDisabled={false}
				/>
				{trainee?.isTrainer && (
					<SelectInput
						control={control}
						name="status"
						label={formatMessage({ id: 'global.fields.status' })}
						options={TRAINEE_STATUSES.map(status => ({
							type: SelectOptionType.OPTION,
							label: formatMessage({ id: `global.enums.status.${status}` }),
							value: status
						}))}
						isRequired
					/>
				)}
			</Form>
			<Buttons>
				<Button onClick={handleSubmit(onSubmit)} variant="secondary">
					<FormattedMessage id="trainee.information.buttons.save.label" />
				</Button>
				<Button
					onClick={() => setIsDeletionPopInOpen(true)}
					variant="tertiary"
				>
					<FormattedMessage id="global.buttons.delete" />
				</Button>
			</Buttons>
			{(isDeletionPopInOpen && id) && (
				<TraineeDeletionPopIn
					id={id}
					onClose={() => setIsDeletionPopInOpen(false)}
					onSuccess={() => navigate(`/${Route.TRAINEES}`)}
					isOpen
				/>
			)}
		</Container>
	)
}

const Container = styled(Stack)({
	rowGap: '20px',
	width: '380px'
})

const Title = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row'
})

const IconContainer = styled(Stack)({
	padding: '10px'
})

const Form = styled(Stack)({
	rowGap: '16px'
})

const Buttons = styled(Stack)({
	rowGap: '8px'
})

export default TraineeInformation
